import { useEffect, useState } from 'react';
import { RefreshCw, ThumbsDown, ThumbsUp } from 'lucide-react';
import { fmt } from '@nyce/admin-kit';
import { Button, Chip, Dialog, Select, Switch, TextField } from '@nyce/admin-kit/ui';
import {
  EmptyState,
  ErrorBox,
  Kpi,
  Loading,
  Page,
  Pager,
  TableScroller,
  useAsync,
  useNotice,
} from '../../../components/common';
import { useTargets } from '../../../identity/session';
import { useBuddyWrite } from '../../api/client';
import { qualityFor, type FeedbackItem, type FeedbackVote } from '../../api/quality';
import { AnswerView } from './AnswerView';
import { useUrlParams, when } from './params';
import '../../buddy.css';
import './quality.css';

const PAGE_SIZE = 25;
const VOTES = ['up', 'down'] as const;
const STATES = ['open', 'handled'] as const;

function Vote({ vote }: { vote: FeedbackVote }) {
  return vote === 'up' ? (
    <Chip size="sm" className="nyk-tone-green" icon={<ThumbsUp size={12} aria-hidden="true" />}>
      up
    </Chip>
  ) : (
    <Chip size="sm" className="nyk-tone-red" icon={<ThumbsDown size={12} aria-hidden="true" />}>
      down
    </Chip>
  );
}

function Status({ f }: { f: FeedbackItem }) {
  if (!f.handledAt)
    return (
      <Chip size="sm" className="nyk-tone-amber">
        open
      </Chip>
    );
  return (
    <span title={`${f.handledBy ?? ''} ${when(f.handledAt)}`}>
      <Chip size="sm" className="nyk-tone-grey">
        handled
      </Chip>
      {f.showToUser && <span className="nyk-ui-hint"> · reply shown</span>}
    </span>
  );
}

/** One feedback: what was asked and answered, the user's comment, and (operator with write) handle or reopen it. */
function FeedbackDialog({
  item,
  canWrite,
  onClose,
  onHandled,
}: {
  item: FeedbackItem;
  canWrite: boolean;
  onClose: () => void;
  onHandled: (handled: boolean, comment: string, showToUser: boolean) => Promise<void>;
}) {
  const [comment, setComment] = useState('');
  const [showToUser, setShowToUser] = useState(false);
  const [busy, setBusy] = useState(false);
  const run = async (handled: boolean) => {
    setBusy(true);
    try {
      await onHandled(handled, comment, showToUser);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={item.userQuestion || 'Feedback'}
      maxWidth={900}
      // a typed reply is not lost to a stray click outside
      closeOnBackdrop={!comment.trim()}
    >
      <div className="bd-meta" data-testid="feedback-meta">
        <Vote vote={item.feedback} />
        <span>{item.userCode}</span>
        <span>{item.systemName ?? item.systemId}</span>
        <span>{when(item.createdAt)}</span>
        {item.model && <span>{item.model}</span>}
        {item.durationMs != null && <span>{(item.durationMs / 1000).toFixed(1)} s</span>}
      </div>
      {item.comment && <blockquote className="bd-quote">“{item.comment}”</blockquote>}
      {item.handledAt && (
        <p className="bd-quote">
          <b>{item.handledBy ?? 'Handled'}</b> {when(item.handledAt)}
          {item.handledComment && `: ${item.handledComment}`}
          {item.showToUser && (
            <span className="nyk-ui-hint">
              {' '}
              · shown to the user{item.userSeenAt ? `, seen ${when(item.userSeenAt)}` : ', not seen yet'}
            </span>
          )}
        </p>
      )}
      {item.customerHandledAt && (
        <p className="bd-quote">
          <b>Customer</b> {when(item.customerHandledAt)}
          {item.customerHandledComment && `: ${item.customerHandledComment}`}
        </p>
      )}
      <AnswerView raw={item.report ?? item.aiResponse} />
      {canWrite && (
        <div className="bd-triage">
          {item.handledAt ? (
            <div className="bd-triage-actions">
              <span className="nyk-ui-hint">Reopen to triage it again.</span>
              <Button variant="ghost" loading={busy} onClick={() => void run(false)}>
                Reopen
              </Button>
            </div>
          ) : (
            <>
              <TextField
                multiline
                rows={3}
                label="Reply"
                placeholder="Admin comment, optional"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <div className="bd-triage-actions">
                <Switch checked={showToUser} onChange={setShowToUser} label="Show the reply to the user" />
                <Button loading={busy} onClick={() => void run(true)}>
                  Mark handled
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </Dialog>
  );
}

/** Thumbs up and down from chat users with the rated answer; filters, page and the open feedback live in the URL. */
export function BuddyFeedbackPage() {
  const { active } = useTargets();
  const canWrite = useBuddyWrite();
  const { get, pick, set } = useUrlParams();
  const vote = pick<FeedbackVote | ''>('feedback', VOTES, '');
  const state = pick<(typeof STATES)[number] | ''>('handled', STATES, '');
  const page = Math.max(1, Number(get('page')) || 1);
  const openId = get('open');
  const api = qualityFor(active);
  const list = useAsync(
    () =>
      api.feedback({
        page,
        pageSize: PAGE_SIZE,
        feedback: vote || undefined,
        handled: state ? state === 'handled' : undefined,
      }),
    [active, page, vote, state]
  );
  const stats = useAsync(() => api.feedbackStats(), [active]);
  const toast = useNotice();
  const items = list.data?.items ?? [];
  const open = items.find((f) => f.id === openId) ?? null;
  // A shared link to feedback that is no longer on this page: drop it rather than keep a dead `open`.
  useEffect(() => {
    if (openId && list.data && !open) set({ open: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId, list.data, open]);

  const filter = (patch: Record<string, string | null>) => set({ ...patch, page: null, open: null });
  const setHandled = async (item: FeedbackItem, handled: boolean, comment: string, showToUser: boolean) => {
    try {
      await api.setHandled(item.id, {
        handled,
        comment: handled ? comment.trim() || null : undefined,
        showToUser: handled ? showToUser : undefined,
      });
      toast.success(handled ? 'Marked handled' : 'Reopened');
      set({ open: null });
      await Promise.all([list.reload(), stats.reload()]);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };
  const s = stats.data;
  return (
    <Page
      title="Feedback"
      description="Thumbs up and down from chat users with the answer they rated, to triage and reply."
      actions={
        <div className="bd-toolbar">
          <Select
            aria-label="Vote"
            value={vote}
            onChange={(v) => filter({ feedback: v })}
            options={[
              { value: '', label: 'All votes' },
              { value: 'up', label: 'Thumbs up' },
              { value: 'down', label: 'Thumbs down' },
            ]}
          />
          <Select
            aria-label="Status"
            value={state}
            onChange={(v) => filter({ handled: v })}
            options={[
              { value: '', label: 'Open and handled' },
              { value: 'open', label: 'Open' },
              { value: 'handled', label: 'Handled' },
            ]}
          />
          <Button
            size="sm"
            variant="ghost"
            icon={<RefreshCw size={14} />}
            loading={list.loading}
            onClick={() => void Promise.all([list.reload(), stats.reload()])}
          >
            Refresh
          </Button>
        </div>
      }
    >
      {toast.element}
      <ErrorBox error={list.error ?? stats.error} />
      {s && (
        <div className="nyk-kpis" data-testid="feedback-stats">
          <Kpi
            label="Total"
            value={fmt(s.total)}
            sub={s.last7Days != null ? `${fmt(s.last7Days)} in 7 days` : undefined}
          />
          <Kpi
            label="Thumbs up"
            value={fmt(s.up)}
            sub={s.total ? `${Math.round((s.up / s.total) * 100)} %` : undefined}
          />
          <Kpi label="Thumbs down" value={fmt(s.down)} />
          <button type="button" className="bd-kpi-btn" onClick={() => filter({ handled: 'open' })}>
            <Kpi label="Open" value={fmt(s.unhandled)} sub="to triage" />
          </button>
        </div>
      )}
      <Loading loading={list.loading && !list.data} />
      <div className="nyk-card">
        <TableScroller>
          <table className="nyk-ui-table nyk-cards" data-testid="feedback-table">
            <thead>
              <tr>
                <th>Question</th>
                <th>Vote</th>
                <th>When</th>
                <th>User</th>
                <th>System</th>
                <th>Comment</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((f) => (
                <tr key={f.id} data-feedback={f.id}>
                  <td className="nyk-card-title">
                    <button type="button" className="bd-title-link" onClick={() => set({ open: f.id })}>
                      <span className="bd-clip">{f.userQuestion || '(no question)'}</span>
                    </button>
                  </td>
                  <td data-label="Vote">
                    <Vote vote={f.feedback} />
                  </td>
                  <td data-label="When">{when(f.createdAt)}</td>
                  <td data-label="User">{f.userCode}</td>
                  <td data-label="System">{f.systemName ?? f.systemId}</td>
                  <td data-label="Comment">
                    <span className="bd-clip">{f.comment}</span>
                  </td>
                  <td data-label="Status">
                    <Status f={f} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroller>
        {!list.loading && !items.length && <EmptyState title="No feedback matches these filters." />}
        <Pager
          page={page}
          pageSize={PAGE_SIZE}
          total={list.data?.total ?? 0}
          onPage={(p) => set({ page: p === 1 ? null : String(p), open: null })}
        />
      </div>
      {open && (
        <FeedbackDialog
          key={open.id}
          item={open}
          canWrite={canWrite}
          onClose={() => set({ open: null })}
          onHandled={(h, c, show) => setHandled(open, h, c, show)}
        />
      )}
    </Page>
  );
}
